import { IEdoChatParticipation } from "@/shared/model/edo-chat-participation.model";
import { IPaginationResponse } from "@/shared/util/pagination.constants";
import { createAsyncThunk, createSlice, isFulfilled, isPending, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
    IQueryParamsWithFilters
} from "./reducer.utils";

export const initialState: {
    participations: IEdoChatParticipation[];
    loadingParticipations: boolean;
    totalItemsParticipations: number;
    selectedParticipation: IEdoChatParticipation | null;
    loadingParticipation: boolean;
    errorMessage: string | null;
    openChatIds: number[];
    minimizedChatIds: number[];
} = {
    participations: [],
    loadingParticipations: false,
    totalItemsParticipations: 0,
    selectedParticipation: null,
    loadingParticipation: false,
    errorMessage: null,
    openChatIds: [],
    minimizedChatIds: [],
};

export type ChatState = Readonly<typeof initialState>;

const apiUrl = "/students";

// Actions

export const getUserParticipations = createAsyncThunk(
    "chat/get_user_participations",
    async ({ page, size, sort, filters }: IQueryParamsWithFilters) => {
        const params = new URLSearchParams(filters);
        if (sort) {
            params.append("page", String(page));
            params.append("limit", String(size));
            if (typeof sort === "string") {
                params.append("sortBy", sort.replace(",", ":"));
            } else {
                for (let s of sort) {
                    params.append("sortBy", s.replace(",", ":"));
                }
            }
        }
        let requestUrl;
        if (params.toString() === "") {
            requestUrl = `/account/chat-participations`;
        } else {
            requestUrl = `/account/chat-participations?${params.toString()}`;
        }
        return axios.get<IPaginationResponse<IEdoChatParticipation>>(
            requestUrl
        );
    }
);

export const getUserParticipationByChat = createAsyncThunk(
    "chat/get_user_participation_by_chat",
    async (id: number | string) => {
        return axios.get<{ data: IEdoChatParticipation; }>(
            `/chats/${id}/participation`
        );
    }
);
// slice

const MAX_OPEN_CHAT_WINDOWS = 3;

export const ChatSlice = createSlice({
    name: "chat",
    initialState: initialState as ChatState,
    reducers: {
        setSelectedParticipation(state, action: PayloadAction<IEdoChatParticipation>) {
            state.selectedParticipation = action.payload;
        },
        openChatWindow(state, action: PayloadAction<number>) {
            const id = action.payload;
            // If minimized, restore it
            state.minimizedChatIds = state.minimizedChatIds.filter((cid) => cid !== id);
            // If already open, move to first slot (most recent)
            const existing = state.openChatIds.indexOf(id);
            if (existing !== -1) {
                state.openChatIds.splice(existing, 1);
            }
            state.openChatIds.unshift(id);
            // Cap to max windows; bumped chats become minimized bubbles
            if (state.openChatIds.length > MAX_OPEN_CHAT_WINDOWS) {
                const overflow = state.openChatIds.splice(MAX_OPEN_CHAT_WINDOWS);
                for (const overflowId of overflow) {
                    if (!state.minimizedChatIds.includes(overflowId)) {
                        state.minimizedChatIds.push(overflowId);
                    }
                }
            }
        },
        closeChatWindow(state, action: PayloadAction<number>) {
            const id = action.payload;
            state.openChatIds = state.openChatIds.filter((cid) => cid !== id);
            state.minimizedChatIds = state.minimizedChatIds.filter((cid) => cid !== id);
        },
        minimizeChatWindow(state, action: PayloadAction<number>) {
            const id = action.payload;
            state.openChatIds = state.openChatIds.filter((cid) => cid !== id);
            if (!state.minimizedChatIds.includes(id)) {
                state.minimizedChatIds.push(id);
            }
        },
        closeAllChatWindows(state) {
            state.openChatIds = [];
            state.minimizedChatIds = [];
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getUserParticipationByChat.fulfilled, (state, action) => {
                state.loadingParticipation = false;
                state.selectedParticipation = action.payload.data.data;
            })
            .addMatcher(
                isFulfilled(getUserParticipations),
                (state, action) => {
                    const {
                        data: {
                            data,
                            meta: { totalItems },
                        },
                        headers,
                    } = action.payload;

                    return {
                        ...state,
                        loadingParticipations: false,
                        participations: data,
                        totalItemsParticipations: totalItems,
                    };
                }
            )
            .addMatcher(
                isPending(getUserParticipations),
                (state) => {
                    state.errorMessage = null;
                    state.loadingParticipations = true;
                }
            )
            .addMatcher(
                isPending(getUserParticipationByChat),
                (state) => {
                    state.errorMessage = null;
                    state.loadingParticipation = true;
                }
            );
    },
});

export const {
    setSelectedParticipation,
    openChatWindow,
    closeChatWindow,
    minimizeChatWindow,
    closeAllChatWindows,
} = ChatSlice.actions;

// Reducer
const ChatReducer = ChatSlice.reducer;
export default ChatReducer;
